import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// Renders the final value immediately; the count-up is an enhancement only,
// so a blocked/throttled script or reduced-motion preference never shows a
// frozen partial number.
export function CountUp({ value, duration = 1100, locale = 'en-US' }) {
  const [display, setDisplay] = useState(value);
  const hasRun = useRef(false);
  const { ref, inView } = useInView({ threshold: 0.4, triggerOnce: true });

  useEffect(() => {
    if (!inView || hasRun.current) return;
    hasRun.current = true;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !window.requestAnimationFrame) {
      setDisplay(value);
      return;
    }

    setDisplay(0);
    let start = null;
    let frame;

    function step(timestamp) {
      if (start === null) start = timestamp;
      const progress = Math.min(1, (timestamp - start) / duration);
      setDisplay(Math.round(value * progress));
      if (progress < 1) frame = requestAnimationFrame(step);
      else setDisplay(value);
    }

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, duration]);

  return (
    <span ref={ref}>
      {display.toLocaleString(locale, { useGrouping: 'always' })}
    </span>
  );
}
